import React, { useState, useEffect } from 'react';
import {
  Copy, Download, Upload, RefreshCw, Eye, EyeOff, ArrowRightLeft, Zap, Shield,
} from 'lucide-react';
import { logAnalyticsEvent } from '../firebase';
import { useTheme } from '../theme';
import { encodeBase64, decodeBase64, Base64Variant } from '../lib/base64';

type Mode = 'encode' | 'decode';

interface Base64ToolProps {
  initialMode?: Mode;
  variant?: Base64Variant;
}

export default function Base64Tool({ initialMode = 'encode', variant = 'standard' }: Base64ToolProps) {
  const { darkMode, themeClasses } = useTheme();
  const [inputText, setInputText] = useState('');
  const [outputText, setOutputText] = useState('');
  const [mode, setMode] = useState<Mode>(initialMode);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);
  const [showOutput, setShowOutput] = useState(true);
  const [isConverting, setIsConverting] = useState(false);

  // Auto-convert with a short debounce for the spinner animation.
  useEffect(() => {
    if (!inputText.trim()) {
      setOutputText('');
      setError('');
      return;
    }

    setIsConverting(true);
    const timer = setTimeout(() => {
      try {
        const result = mode === 'encode'
          ? encodeBase64(inputText, variant)
          : decodeBase64(inputText, variant);
        setOutputText(result);
        setError('');
      } catch (err) {
        setError(mode === 'decode' ? 'Invalid Base64 string' : 'Encoding error');
        setOutputText('');
      }
      setIsConverting(false);
    }, 200);

    return () => clearTimeout(timer);
  }, [inputText, mode, variant]);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(outputText);
      setCopied(true);
      logAnalyticsEvent('copy_text', { mode, variant, text_length: outputText.length });
      setTimeout(() => setCopied(false), 2000);
    } catch (err: any) {
      console.error('Failed to copy:', err);
      logAnalyticsEvent('copy_error', { error: err.message });
    }
  };

  const handleDownload = () => {
    const blob = new Blob([outputText], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${mode}d-text.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    logAnalyticsEvent('download_file', { mode, variant, file_size: outputText.length });
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files && files[0]) {
      const file = files[0];
      const reader = new FileReader();
      reader.onload = (e) => {
        const result = e.target?.result;
        setInputText(typeof result === 'string' ? result : '');
        logAnalyticsEvent('file_upload', { file_type: file.type, file_size: file.size });
      };
      reader.readAsText(file);
    }
  };

  const handleClear = () => {
    setInputText('');
    setOutputText('');
    setError('');
    logAnalyticsEvent('clear_text');
  };

  const switchMode = () => {
    const newMode: Mode = mode === 'encode' ? 'decode' : 'encode';
    setMode(newMode);
    logAnalyticsEvent('switch_mode', { from_mode: mode, to_mode: newMode, variant });
    if (outputText && !error) {
      setInputText(outputText);
    }
  };

  const encodeLabel = variant === 'url-safe'
    ? 'Encode to URL-Safe Base64'
    : variant === 'mime'
      ? 'Encode to MIME Base64'
      : 'Encode to Base64';
  const decodeLabel = variant === 'url-safe'
    ? 'Decode from URL-Safe Base64'
    : variant === 'mime'
      ? 'Decode from MIME Base64'
      : 'Decode from Base64';
  const samplePlaceholder = mode === 'encode'
    ? 'Type or paste your text here...\n\nTry: "Hello, World!"'
    : variant === 'url-safe'
      ? 'Paste your URL-safe Base64 string here...\n\nTry: "SGVsbG8sIFdvcmxkIQ"'
      : variant === 'mime'
        ? 'Paste your MIME Base64 string here (line breaks OK)...\n\nTry: "SGVsbG8sIFdvcmxkIQ=="'
        : 'Paste your Base64 string here...\n\nTry: "SGVsbG8sIFdvcmxkIQ=="';

  return (
    <div className={`${themeClasses.cardBg} rounded-3xl border p-5 sm:p-8 transition-colors`}>
      <div>
        {/* Mode selector */}
        <div className="flex items-center justify-center mb-8">
          <div className={`${themeClasses.modeSelectorBg} rounded-2xl p-2 border w-full sm:w-auto`}>
            <div className="flex items-center">
              <button
                onClick={() => setMode('encode')}
                className={`flex-1 sm:flex-none px-4 sm:px-8 py-3 sm:py-4 rounded-xl font-semibold transition-colors flex items-center justify-center gap-2 whitespace-nowrap ${
                  mode === 'encode'
                    ? themeClasses.accent
                    : `${themeClasses.textSecondary}`
                }`}
              >
                <Zap size={18} className="shrink-0" />
                <span className="sm:hidden">Encode</span>
                <span className="hidden sm:inline">{encodeLabel}</span>
              </button>
              <div className="hidden sm:block mx-2">
                <ArrowRightLeft className={`w-5 h-5 ${themeClasses.textMuted}`} />
              </div>
              <button
                onClick={() => setMode('decode')}
                className={`flex-1 sm:flex-none px-4 sm:px-8 py-3 sm:py-4 rounded-xl font-semibold transition-colors flex items-center justify-center gap-2 whitespace-nowrap ${
                  mode === 'decode'
                    ? themeClasses.accent
                    : `${themeClasses.textSecondary}`
                }`}
              >
                <Shield size={18} className="shrink-0" />
                <span className="sm:hidden">Decode</span>
                <span className="hidden sm:inline">{decodeLabel}</span>
              </button>
            </div>
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-8">
          {/* Input */}
          <div className="space-y-4">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <label className={`block text-lg font-semibold ${themeClasses.textPrimary}`}>
                {mode === 'encode' ? '📝 Input Text' : '🔐 Base64 Input'}
              </label>
              <div className="flex gap-2 sm:gap-3">
                <label className={`cursor-pointer ${themeClasses.accentText} hover:opacity-80 flex items-center gap-2 px-3 py-1 rounded-lg ${themeClasses.buttonSecondary} backdrop-blur-sm border transition-all hover:scale-105`}>
                  <Upload size={16} />
                  Upload
                  <input type="file" accept=".txt" onChange={handleFileUpload} className="hidden" />
                </label>
                <button
                  onClick={handleClear}
                  className={`${themeClasses.textMuted} flex items-center gap-2 px-3 py-1 rounded-lg ${themeClasses.buttonSecondary} backdrop-blur-sm border transition-all hover:scale-105`}
                >
                  <RefreshCw size={16} />
                  Clear
                </button>
              </div>
            </div>

            <div className="relative">
              <textarea
                value={inputText}
                onChange={(e) => setInputText(e.target.value)}
                placeholder={samplePlaceholder}
                aria-label={mode === 'encode' ? 'Text to encode' : 'Base64 to decode'}
                className={`w-full h-40 p-4 ${themeClasses.inputBg} backdrop-blur-sm border rounded-2xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent resize-none transition-all duration-300`}
              />
              {isConverting && (
                <div className="absolute top-4 right-4">
                  <div className={`w-6 h-6 border-2 ${darkMode ? 'border-indigo-400 border-t-transparent' : 'border-indigo-600 border-t-transparent'} rounded-full animate-spin`}></div>
                </div>
              )}
            </div>

            <div className="flex justify-between items-center text-sm">
              <span className={themeClasses.textMuted}>{`${inputText.length.toLocaleString()} characters`}</span>
              {inputText && (
                <button
                  onClick={switchMode}
                  className={`${themeClasses.accentText} hover:opacity-80 flex items-center gap-2 px-3 py-1 rounded-lg ${themeClasses.buttonSecondary} backdrop-blur-sm border transition-all hover:scale-105`}
                >
                  <ArrowRightLeft size={14} />
                  Switch Mode
                </button>
              )}
            </div>
          </div>

          {/* Output */}
          <div className="space-y-4">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <label className={`block text-lg font-semibold ${themeClasses.textPrimary}`}>
                {mode === 'encode' ? '🔐 Base64 Output' : '📝 Decoded Text'}
              </label>
              {outputText && (
                <div className="flex gap-2 sm:gap-3">
                  <button
                    onClick={() => setShowOutput(!showOutput)}
                    className={`${themeClasses.textMuted} flex items-center gap-2 px-3 py-1 rounded-lg ${themeClasses.buttonSecondary} backdrop-blur-sm border transition-all hover:scale-105`}
                  >
                    {showOutput ? <EyeOff size={16} /> : <Eye size={16} />}
                    {showOutput ? 'Hide' : 'Show'}
                  </button>
                  <button
                    onClick={handleCopy}
                    className={`flex items-center gap-2 px-3 py-1 rounded-lg ${themeClasses.buttonSecondary} backdrop-blur-sm border transition-all hover:scale-105 ${
                      copied ? 'text-green-400' : `${themeClasses.accentText} hover:opacity-80`
                    }`}
                  >
                    <Copy size={16} />
                    {copied ? 'Copied!' : 'Copy'}
                  </button>
                  <button
                    onClick={handleDownload}
                    className={`${darkMode ? 'text-green-400 hover:text-green-300' : 'text-green-600 hover:text-green-700'} flex items-center gap-2 px-3 py-1 rounded-lg ${themeClasses.buttonSecondary} backdrop-blur-sm border transition-all hover:scale-105`}
                  >
                    <Download size={16} />
                    Save
                  </button>
                </div>
              )}
            </div>

            {error ? (
              <div className={`p-4 ${themeClasses.errorBg} border rounded-2xl backdrop-blur-sm`}>
                <div className="flex items-center gap-2">
                  <div className={`w-2 h-2 ${darkMode ? 'bg-red-400' : 'bg-red-500'} rounded-full`}></div>
                  {error}
                </div>
              </div>
            ) : (
              <div className="relative">
                <textarea
                  value={showOutput ? outputText : '•'.repeat(Math.min(outputText.length, 50))}
                  readOnly
                  placeholder={`${mode === 'encode' ? 'Base64 encoded' : 'Decoded'} result will appear here...`}
                  aria-label={mode === 'encode' ? 'Base64 output' : 'Decoded text output'}
                  className={`w-full h-40 p-4 ${themeClasses.inputBg} backdrop-blur-sm border rounded-2xl resize-none`}
                />
                {outputText && (
                  <div className="absolute top-4 right-4">
                    <div className="w-3 h-3 bg-green-400 rounded-full animate-pulse"></div>
                  </div>
                )}
              </div>
            )}

            {outputText && (
              <div className={`text-sm ${themeClasses.textMuted}`}>
                {`${outputText.length.toLocaleString()} characters`}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
